import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PasswordInput from "@/components/PasswordInput";
import Logo from "@/components/Logo";
import { useAuth, fmtErr } from "@/lib/auth";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center justify-center mb-10 w-full">
          <Logo variant="name" height={112} testid="auth-logo" />
        </Link>
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <h1 className="font-heading text-2xl tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
        {footer && <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>}
      </div>
    </div>
  );
}

export function LoginPage() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(email, pw); toast.success("Willkommen zurück."); nav("/app"); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Anmelden" subtitle="Melde dich mit deiner E-Mail-Adresse an."
      footer={<>Noch kein Konto? <Link to="/registrieren" className="text-foreground underline">Registrieren</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <div><Label>E-Mail</Label><Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="login-email" className="mt-1.5" autoComplete="email" /></div>
        <div><Label>Passwort</Label><PasswordInput required value={pw} onChange={(e)=>setPw(e.target.value)} data-testid="login-password" testIdToggle="login-password-toggle" className="mt-1.5" autoComplete="current-password" /></div>
        {err && <div className="text-sm text-[hsl(0_66%_72%)]" data-testid="login-error">{err}</div>}
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-full" data-testid="login-submit">
          {busy ? "Anmelden…" : "Anmelden"}
        </Button>
        <div className="text-center"><Link to="/passwort-vergessen" className="text-sm text-muted-foreground hover:text-foreground">Passwort vergessen?</Link></div>
      </form>
    </AuthShell>
  );
}

const STAGE_OPTIONS = [
  { slug: "orientierung", label: "Orientierung", hint: "Ich baue mein Fundament auf" },
  { slug: "struktur",     label: "Struktur",     hint: "Ich strukturiere meine Erfahrung" },
  { slug: "optimierung",  label: "Optimierung",  hint: "Ich optimiere gezielt meine Performance" },
];

const GOAL_OPTIONS = [
  { slug: "verstehen",      label: "Meine Strategie klar verstehen" },
  { slug: "konsistenz",     label: "Konsistente Ausführung erreichen" },
  { slug: "profitabilitaet", label: "Nachhaltig profitabel handeln" },
  { slug: "wachstum",       label: "Mein Konto skalieren" },
];

export function RegisterPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const preselectedStage = new URLSearchParams(loc.search).get("stage");
  const validStage = STAGE_OPTIONS.find((s) => s.slug === preselectedStage)?.slug || "";
  const cameFromLanding = !!validStage;

  const [f, setF] = useState({
    email: "", password: "", password_confirm: "",
    accept_terms: false, accept_privacy: false,
    stage: validStage, goal: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    if (!f.stage) {
      setErr("Bitte wähle eine Nutzergruppe (Orientierung, Struktur oder Optimierung).");
      setBusy(false);
      return;
    }
    try {
      await axios.post(`${API}/auth/register`, {
        ...f,
        stage: f.stage,
        goal: f.goal || null,
        terms_version: "1.0", privacy_version: "1.0",
      });
      toast.success("Registrierung erfolgreich. Bitte prüfe deine E-Mails.");
      nav("/anmelden");
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const stageLabel = STAGE_OPTIONS.find((s) => s.slug === validStage);

  return (
    <AuthShell title="Kostenlos starten" subtitle="Lege dein TradeMind-Konto an."
      footer={<>Bereits registriert? <Link to="/anmelden" className="text-foreground underline">Anmelden</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        {cameFromLanding && stageLabel && (
          <div
            className="rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 flex items-start gap-3"
            data-testid="reg-stage-chip"
          >
            <div className="h-8 w-8 rounded-full bg-primary/20 grid place-items-center text-primary text-xs font-mono">
              {stageLabel.label.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="text-sm">
                <span className="text-muted-foreground">Dein Fokus: </span>
                <span className="font-medium text-foreground">{stageLabel.label}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{stageLabel.hint}</p>
            </div>
            <Link
              to="/registrieren"
              className="text-xs text-muted-foreground hover:text-foreground underline shrink-0"
              data-testid="reg-stage-change"
            >
              Ändern
            </Link>
          </div>
        )}

        {!cameFromLanding && (
          <>
            <div>
              <Label>Wo stehst du gerade? <span className="text-[hsl(0_66%_72%)]">*</span></Label>
              <div className="mt-1.5 grid grid-cols-1 gap-2">
                {STAGE_OPTIONS.map((opt) => {
                  const active = f.stage === opt.slug;
                  return (
                    <button
                      key={opt.slug}
                      type="button"
                      onClick={() => set("stage", active ? "" : opt.slug)}
                      aria-pressed={active}
                      className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                        active
                          ? "border-primary/60 bg-primary/10"
                          : "border-border bg-secondary/40 hover:bg-secondary"
                      }`}
                      data-testid={`reg-stage-${opt.slug}`}
                    >
                      <div className="text-sm font-medium text-foreground">{opt.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{opt.hint}</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Pflichtfeld — dein Coach passt sich diesem Fokus an.</p>
            </div>

            <div>
              <Label>Wo siehst du dich in 12 Monaten?</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {GOAL_OPTIONS.map((opt) => {
                  const active = f.goal === opt.slug;
                  return (
                    <button
                      key={opt.slug}
                      type="button"
                      onClick={() => set("goal", active ? "" : opt.slug)}
                      aria-pressed={active}
                      className={`rounded-full border px-3 h-9 text-xs transition-colors ${
                        active
                          ? "border-primary/60 bg-primary/15 text-foreground"
                          : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`reg-goal-${opt.slug}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <div><Label>E-Mail</Label><Input type="email" required value={f.email} onChange={(e)=>set("email",e.target.value)} data-testid="reg-email" className="mt-1.5" autoComplete="email" /></div>
        <div><Label>Passwort</Label><PasswordInput required minLength={12} value={f.password} onChange={(e)=>set("password",e.target.value)} data-testid="reg-password" testIdToggle="reg-password-toggle" className="mt-1.5" autoComplete="new-password" />
          <p className="text-xs text-muted-foreground mt-1.5">Mindestens 12 Zeichen. Empfehlung: kombiniere Groß-, Kleinbuchstaben, Ziffern und Sonderzeichen. Passwortmanager sind willkommen.</p></div>
        <div><Label>Passwort bestätigen</Label><PasswordInput required value={f.password_confirm} onChange={(e)=>set("password_confirm",e.target.value)} data-testid="reg-password-confirm" testIdToggle="reg-password-confirm-toggle" className="mt-1.5" autoComplete="new-password" /></div>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox checked={f.accept_terms} onCheckedChange={(v)=>set("accept_terms",!!v)} data-testid="reg-terms" />
          <span>Ich akzeptiere die <Link to="/agb" className="underline" target="_blank">Nutzungsbedingungen</Link>.</span>
        </label>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox checked={f.accept_privacy} onCheckedChange={(v)=>set("accept_privacy",!!v)} data-testid="reg-privacy" />
          <span>Ich habe die <Link to="/datenschutz" className="underline" target="_blank">Datenschutzerklärung</Link> gelesen und stimme zu.</span>
        </label>
        {err && <div className="text-sm text-[hsl(0_66%_72%)]" data-testid="reg-error">{err}</div>}
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-full" data-testid="reg-submit">
          {busy ? "Konto wird erstellt…" : "Kostenlos starten"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">Nach der Registrierung senden wir dir eine E-Mail mit einem Bestätigungslink.</p>
      </form>
    </AuthShell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false); const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/auth/forgot-password`, { email }); setDone(true); }
    catch { setDone(true); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Passwort vergessen" subtitle="Wir senden dir einen Link zum Zurücksetzen."
      footer={<Link to="/anmelden" className="text-foreground underline">Zurück zur Anmeldung</Link>}>
      {done ? <p className="text-sm text-muted-foreground" data-testid="forgot-done">Wenn ein Konto existiert, wurde eine E-Mail versendet. Bitte prüfe auch den Spam-Ordner.</p> :
        <form onSubmit={submit} className="space-y-4">
          <div><Label>E-Mail</Label><Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="forgot-email" className="mt-1.5" /></div>
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-full" data-testid="forgot-submit">{busy?"Senden…":"Link anfordern"}</Button>
        </form>}
    </AuthShell>
  );
}

export function ResetPage() {
  const loc = useLocation(); const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setErr("Passwörter stimmen nicht überein."); return; }
    setBusy(true); setErr("");
    try { await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      toast.success("Passwort geändert. Bitte melde dich an."); nav("/anmelden"); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Neues Passwort festlegen" subtitle="Wähle ein sicheres Passwort für dein Konto.">
      {!token ? <p className="text-sm text-[hsl(0_66%_72%)]" data-testid="reset-no-token">Kein Token vorhanden. Bitte fordere einen neuen Link an.</p> :
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Neues Passwort</Label><PasswordInput required minLength={12} value={pw} onChange={(e)=>setPw(e.target.value)} data-testid="reset-password" testIdToggle="reset-password-toggle" className="mt-1.5" autoComplete="new-password" />
            <p className="text-xs text-muted-foreground mt-1.5">Mindestens 12 Zeichen, Kombination aus verschiedenen Zeichenarten.</p></div>
          <div><Label>Passwort bestätigen</Label><PasswordInput required value={pw2} onChange={(e)=>setPw2(e.target.value)} data-testid="reset-password-confirm" testIdToggle="reset-password-confirm-toggle" className="mt-1.5" autoComplete="new-password" /></div>
          {err && <div className="text-sm text-[hsl(0_66%_72%)]" data-testid="reset-error">{err}</div>}
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-full" data-testid="reset-submit">{busy?"Speichern…":"Passwort speichern"}</Button>
        </form>}
    </AuthShell>
  );
}

export function VerifyPage() {
  const loc = useLocation();
  // Token kann als Path-Param (/anmeldung/bestaetigen/:token) ODER als
  // Query-Param (?token=...) kommen — Legacy-Mails haben ?token=.
  const params = new URLSearchParams(loc.search);
  const pathToken = loc.pathname.split("/").filter(Boolean).pop();
  const token = params.get("token") || (pathToken && pathToken !== "bestaetigen" ? pathToken : "");
  const [status, setStatus] = useState("pending");
  const [msg, setMsg] = useState("");
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return; // React-18-StrictMode: useEffect läuft im Dev doppelt
    fired.current = true;
    if (!token) { setStatus("fail"); setMsg("Kein Bestätigungstoken vorhanden."); return; }
    axios.get(`${API}/auth/verify/${token}`)
      .then((r)=>{ setStatus("ok"); setMsg(r.data.message); })
      .catch((e)=>{ setStatus("fail"); setMsg(fmtErr(e.response?.data?.detail) || "Bestätigung fehlgeschlagen."); });
  }, [token]);
  return (
    <AuthShell title={status==="ok"?"E-Mail bestätigt":status==="fail"?"Bestätigung fehlgeschlagen":"E-Mail wird bestätigt…"}>
      <p className="text-sm text-muted-foreground" data-testid="verify-msg">{msg || "Einen Moment bitte…"}</p>
      {status !== "pending" && <Link to="/anmelden"><Button className="mt-4 rounded-full">Zur Anmeldung</Button></Link>}
    </AuthShell>
  );
}
